import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Modal para edição de marca.
 * Permite atualizar status (enable) e imagem da marca.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Controla visibilidade do modal
 * @param {Function} props.onClose - Callback ao fechar modal
 * @param {Object} props.brand - Dados da marca
 * @param {Function} props.onSave - Callback ao salvar alterações
 * @returns {JSX.Element}
 */
export default function BrandEditModal({ open, onClose, brand, onSave }) {
  const [enable, setEnable] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (brand) {
      setEnable(Boolean(brand.enable));
      const imageUrl = brand.image_url 
        ? `${brand.image_url.split('?')[0]}?v=${Date.now()}` 
        : null;
      setImagePreview(imageUrl);
      setImageBase64(null);
    }
  }, [brand]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setError('Formato inválido. Use PNG, JPEG ou JPG');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Imagem muito grande. Máximo: 5MB');
      return;
    }

    setError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImageBase64(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      await onSave({
        id: brand.id,
        enable,
        brand_image: imageBase64
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao atualizar marca');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setImagePreview(null);
    setImageBase64(null);
    onClose();
  };

  if (!brand) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Editar Marca
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Nome da Marca"
          value={brand.name}
          fullWidth
          disabled
          sx={{ mb: 3 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={enable}
              onChange={(e) => setEnable(e.target.checked)}
            />
          }
          label="Marca Ativa"
          sx={{ mb: 3 }}
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Imagem da Marca
          </Typography>

          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              position: 'relative',
              minHeight: 200,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {imagePreview ? (
              <Box
                component="img"
                src={imagePreview}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain'
                }}
              />
            ) : (
              <PhotoCameraIcon sx={{ fontSize: 60, color: 'grey.400' }} />
            )}

            <input
              accept="image/png,image/jpeg,image/jpg"
              style={{ display: 'none' }}
              id="brand-image-upload"
              type="file"
              onChange={handleImageChange}
            />
            <label htmlFor="brand-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCameraIcon />}
                sx={{ mt: 2 }}
              >
                Selecionar Imagem
              </Button>
            </label>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Atualizar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
